// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the plans and "Your plan" panels (`docs/AYNI_XR_PRODUCTS.md`).
 *
 * **Plans** looks like a self-service shop, by design: tabs for who it is
 * for, plan cards with what each includes (from `xrPlans`, the one table),
 * what the live data covers, and a checkout that asks for an invoice (no
 * packages are picked since 2026-10-17: Plus has Live Earth and the climate
 * highlights, every other plan every stream). The request goes to `/api/plans/request` and comes back
 * with a reference. AYNI (phase 4 of the Plus plan): where the build has
 * Paddle's settings (`billing.billingAvailable`), the plans sold online
 * (owner, 2026-10-07: Plus, Teacher, School, Researcher, Lab;
 * `billingCatalog`) are bought online: Plus yearly or monthly, the rest
 * yearly, each visitor's price with their country's VAT (`previewPrices`,
 * `previewChoice`), and Buy → sign in if needed → Paddle's overlay checkout
 * (`startCheckout`). Their cards can still ask for an invoice; the other
 * plans stay by invoice or quote.
 *
 * **Your plan** shows the signed-in account's AYNI XR licence (`account`):
 * the plan, who it is for, its packages and paid-until date, with "Start a
 * meeting" when the plan hosts, and "Remove from my account" to give the
 * seat back. AYNI: an account with a Paddle subscription also sees it
 * (renews, ends, payment failed) and "Manage subscription", which opens
 * Paddle's billing portal (`openBillingPortal`). Without a licence it takes a key; signed out, it offers the
 * sign-in first. **Sign in** (Google, or an emailed link) and **Delete
 * account** are dialogs here too.
 *
 * Both are modal dialogs over the page, opened from the account menu, and
 * Plans also from a link (`?plans`, `?plans=education`). Lives in
 * `src/ui/` so `check:i18n-strings` covers it.
 */

import { getLocale, t, type MessageKey } from '../i18n'
import { isVrActive, notifyXr } from '../services/vrSession'
import { streamQuotaToday } from '../services/planAccess'
import { PACKAGE_WORDS, PLAN_NAME, SEGMENT_WORDS, money, planLines, planName, priceNotes, priceText, seatsText, volumeText } from './xrPlanWords'
import { formatNumber } from '../i18n/format'
import {
  CREDIT_PACK,
  XR_CAPABILITIES,
  XR_PACKAGES,
  XR_PLANS,
  PUBLIC_SEGMENTS,
  XR_SEGMENTS,
  plansOf,
  quote,
  type PlanUse,
  type XrCapability,
  type XrPackage,
  type XrPlan,
  type XrPlanId,
  type XrSegment,
} from '../services/xrPlans'
import {
  GOOGLE_SIGN_IN,
  accountState,
  addLicenceKey,
  deleteAccount,
  loadAccount,
  licenceSwitchBlocked,
  removeLicenceKey,
  requestSignInLink,
  type BillingSummary,
  useLicence,
  type AccountState,
  type LicenceError,
  type LicenceNotice,
  type LicenceSummary,
  type SignInError,
} from '../services/account'
import {
  billingAvailable,
  fetchSharedKey,
  openBillingPortal,
  planOnline,
  previewChoice,
  previewPrices,
  priceIdFor,
  startCheckout,
  type CheckoutError,
  type PricePreview,
} from '../services/billing'
import { normaliseChoice, planPriceKey, type BillingInterval, type OnlinePlanId, type PlanChoice } from '../services/billingCatalog'
import { askStepUp } from './myDataStepUp'
import { attachChallenge } from '../services/turnstile'

export interface PlansOptions {
  /** Start a meeting (the meeting panel), offered when the plan hosts. */
  onStartMeeting?: (trigger: HTMLElement) => void
  /** AYNI: a line shown above the plans, saying why they opened (a stream outside the plan, a tool that needs one). */
  notice?: string
  /** Send an invoice request; for tests. */
  post?: (body: Record<string, unknown>) => Promise<{ ok: boolean; body: Record<string, unknown> }>
}

const defaultPost: NonNullable<PlansOptions['post']> = async (body) => {
  try {
    const res = await fetch('/api/plans/request', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    return { ok: res.ok, body: (await res.json().catch(() => ({}))) as Record<string, unknown> }
  } catch {
    return { ok: false, body: {} }
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function button(className: string, text: string): HTMLButtonElement {
  const b = el('button', className, text)
  b.type = 'button'
  return b
}

// -------------------------------------------------------------- words

// AYNI (2026-10-15): names, prices and card lines live in `xrPlanWords`, shared with /pricing.
export { planLines, planName }

const LICENCE_ERROR: Record<LicenceError, MessageKey> = {
  invalid_key: 'xr.mine.error.invalidKey',
  wrong_product: 'xr.mine.error.wrongProduct',
  redeem_expired: 'xr.mine.error.redeemExpired',
  transfer_limit: 'xr.mine.error.transferLimit',
  seat_taken: 'xr.mine.error.seatTaken',
  license_suspended: 'xr.mine.error.licenseSuspended',
  license_revoked: 'xr.mine.error.licenseRevoked',
  license_expired: 'xr.mine.error.licenseExpired',
  unreachable: 'xr.mine.error.unreachable',
  unknown: 'xr.mine.error.unknown',
  too_many_licences: 'xr.mine.error.tooMany',
}
const CHECKOUT_ERROR: Record<CheckoutError, MessageKey> = {
  signed_out: 'xr.mine.error.signedOut',
  already_subscribed: 'xr.buy.already',
  has_licence: 'xr.buy.error.hasLicence',
  too_many_licences: 'xr.buy.error.tooManyLicences',
  too_many: 'xr.buy.error.tooMany',
  academic_email: 'xr.buy.error.academicEmail',
  unavailable: 'xr.buy.error.unavailable',
  unreachable: 'xr.buy.error.unreachable',
}
const SIGN_IN_ERROR: Record<SignInError, MessageKey> = {
  invalid_email: 'account.signIn.error.invalidEmail',
  too_many: 'account.signIn.error.tooMany',
  mail_unavailable: 'account.signIn.error.mailUnavailable',
  mail_failed: 'account.signIn.error.mailUnavailable',
  unreachable: 'account.signIn.error.unreachable',
  challenge_failed: 'account.signIn.error.challengeFailed',
  link_invalid: 'account.signIn.error.linkInvalid',
  link_expired: 'account.signIn.error.linkExpired',
  link_used: 'account.signIn.error.linkUsed',
  google_denied: 'account.signIn.error.googleDenied',
  google_state: 'account.signIn.error.googleFailed',
  google_failed: 'account.signIn.error.googleFailed',
  google_unavailable: 'account.signIn.error.googleUnavailable',
}

// ------------------------------------------------------------------- dialog

let open: { close(): void } | null = null

/** A modal dialog with a title and a close button; Escape and the backdrop close it, and focus returns to `trigger`. */
export function dialog(titleText: string, trigger: HTMLElement | null, className: string): { panel: HTMLElement; body: HTMLElement; close(): void } {
  open?.close()
  const backdrop = el('div', 'xr-plans-backdrop')
  const panel = el('section', `xr-plans-panel ${className}`)
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  const title = el('h2', 'xr-plans-title', titleText)
  title.id = `xr-plans-title-${className}`
  panel.setAttribute('aria-labelledby', title.id)
  const close = button('xr-plans-close', '✕')
  close.setAttribute('aria-label', t('xr.plans.close'))
  const header = el('header', 'xr-plans-header')
  header.append(title, close)
  const body = el('div', 'xr-plans-body')
  panel.append(header, body)
  document.body.append(backdrop, panel)
  const onKey = (ev: KeyboardEvent): void => {
    // AYNI (2026-10-20): a panel removed without closing (the page replaced it) lets go of the keys, as the figure and cite dialogs do.
    if (!panel.isConnected) {
      document.removeEventListener('keydown', onKey, true)
      return
    }
    if (ev.key === 'Escape') {
      ev.stopPropagation()
      handle.close()
    }
  }
  const handle = {
    panel,
    body,
    close() {
      document.removeEventListener('keydown', onKey, true)
      backdrop.remove()
      panel.remove()
      if (open === handle) open = null
      trigger?.focus()
    },
  }
  close.addEventListener('click', () => handle.close())
  backdrop.addEventListener('click', () => handle.close())
  document.addEventListener('keydown', onKey, true)
  open = handle
  close.focus()
  return handle
}

/** Close whichever plans panel is open. */
export function closePlans(): void {
  open?.close()
}

// -------------------------------------------------------------------- plans

/** AYNI: a capability key's name, for the "Extras" line of Your plan. */
export const CAPABILITY_NAME: Record<XrCapability, MessageKey> = {
  'xr-cap-data': 'xr.cap.data',
  'xr-cap-analysis-full': 'xr.cap.analysisFull',
  'xr-cap-exports-all': 'xr.cap.exportsAll',
  'xr-cap-globes-4': 'xr.cap.globes4',
  'xr-cap-lessons': 'xr.cap.lessons',
  'xr-cap-classroom': 'xr.cap.classroom',
  'xr-cap-games': 'xr.cap.games',
  'xr-cap-stage': 'xr.cap.stage',
  'xr-cap-chatbot': 'xr.cap.chatbot',
  'xr-cap-live': 'xr.cap.live',
  'xr-cap-host-40': 'xr.cap.host40',
  'xr-cap-host-100': 'xr.cap.host100',
  'xr-cap-host-500': 'xr.cap.host500',
  'xr-cap-khipu-keep': 'xr.cap.khipuKeep',
  'xr-cap-research': 'xr.cap.research',
  'xr-cap-own-data': 'xr.cap.ownData',
}

/** AYNI: the licence's add-on keys in words, or null when it carries none. */
export function extrasLine(caps: readonly string[] | undefined): string | null {
  const known = (caps ?? []).filter((c): c is XrCapability => (XR_CAPABILITIES as readonly string[]).includes(c))
  if (known.length === 0) return null
  return t('xr.mine.extras', { list: known.map((c) => t(CAPABILITY_NAME[c])).join(', ') })
}

/**
 * The tab the plans open on when the caller names none (owner, 2026-10-06,
 * D12): the plan of the licence the account holds, else Free and Plus.
 */
function homeSegment(): XrSegment {
  const licence = accountState().licence
  return licence?.status === 'active' && licence.plan ? XR_PLANS[licence.plan].segment : 'public'
}

/**
 * Open the plans, at a segment's tab: the one asked for (a lock names the
 * plans that unlock it), else the account's own plan, else Free and Plus.
 */
export function openPlans(trigger: HTMLElement | null = null, segment?: XrSegment, opts: PlansOptions = {}): void {
  // AYNI (the AR refresh): over a phone's camera or in a headset this page is
  // not drawn; say why it would have opened, and where to see the plans.
  if (isVrActive()) {
    notifyXr(opts.notice ? `${opts.notice} ${t('vr.notice.plansLater')}` : t('vr.notice.plansLater'))
    return
  }
  const d = dialog(t('xr.plans.title'), trigger, 'xr-plans')
  const post = opts.post ?? defaultPost
  // Venue opens only for an account that holds it (owner, 2026-10-18: not shown to buyers).
  let current: XrSegment = segment && (PUBLIC_SEGMENTS.includes(segment) || segment === homeSegment()) ? segment : homeSegment()
  /** Researcher (owner, 2026-10-17): academic or commercial use, two prices for one licence. */
  let use: PlanUse = 'academic'
  // AYNI (phase 4): plans sold online through Paddle (owner, 2026-10-07:
  // Plus, Education and Research). Plus is yearly or monthly, yearly first:
  // it is a third cheaper and pays the payment fee once (plan section 10).
  const online = billingAvailable()
  let interval: BillingInterval = 'year'
  let preview: PricePreview | null = null
  if (online) {
    void previewPrices().then((prices) => {
      preview = prices
      fillVat()
    })
  }

  /** What the dialog holds for a plan sold online: its period and use, as sold. */
  function choiceFor(planId: OnlinePlanId): PlanChoice {
    return normaliseChoice(planId, planId === 'plus' ? interval : 'year', use)
  }

  /** Each visitor's price with their country's VAT, where Paddle has said it. */
  function fillVat(): void {
    for (const line of d.body.querySelectorAll<HTMLElement>('.xr-plan-vat[data-price]')) {
      const total = preview?.[line.dataset.price ?? '']
      line.textContent = total ? t(line.dataset.interval === 'month' ? 'xr.plans.withVatMonth' : 'xr.plans.withVatYear', { amount: total }) : ''
    }
  }

  /** Yearly or monthly, for Plus online. */
  function intervalToggle(onChange: () => void): HTMLElement {
    const group = el('div', 'xr-plan-interval')
    group.setAttribute('role', 'group')
    group.setAttribute('aria-label', t('xr.plans.interval.aria'))
    for (const value of ['year', 'month'] as const) {
      const b = button(`xr-plan-interval-option${value === interval ? ' selected' : ''}`, t(value === 'year' ? 'xr.plans.interval.year' : 'xr.plans.interval.month'))
      b.dataset.interval = value
      b.setAttribute('aria-pressed', String(value === interval))
      b.addEventListener('click', () => {
        if (interval === value) return
        interval = value
        onChange()
        ;(d.body.querySelector(`.xr-plan-interval-option[data-interval="${value}"]`) as HTMLElement | null)?.focus()
      })
      group.append(b)
    }
    return group
  }

  /** Academic or commercial, for Researcher online. */
  function useToggle(onChange: () => void): HTMLElement {
    const group = el('div', 'xr-plan-interval xr-plan-use')
    group.setAttribute('role', 'group')
    group.setAttribute('aria-label', t('xr.plans.use.aria'))
    for (const value of ['academic', 'commercial'] as const) {
      const b = button(`xr-plan-interval-option${value === use ? ' selected' : ''}`, t(value === 'academic' ? 'xr.plans.use.academic' : 'xr.plans.use.commercial'))
      b.dataset.use = value
      b.setAttribute('aria-pressed', String(value === use))
      b.addEventListener('click', () => {
        if (use === value) return
        use = value
        onChange()
        ;(d.body.querySelector(`.xr-plan-interval-option[data-use="${value}"]`) as HTMLElement | null)?.focus()
      })
      group.append(b)
    }
    return group
  }

  /**
   * A plan's price for the chosen period and use, and the line Paddle fills
   * with this visitor's VAT; Plus's prices already include it (owner,
   * 2026-10-17), so it has none.
   */
  function onlinePrice(planId: OnlinePlanId, into: HTMLElement): void {
    const plan = XR_PLANS[planId]
    if (planId === 'plus') {
      const amount = interval === 'year' ? plan.yearly ?? 0 : plan.monthly ?? 0
      into.append(el('p', 'xr-plan-price', t(interval === 'year' ? 'xr.plans.perYearVat' : 'xr.plans.perMonthVatMain', { amount: money(amount) })))
      return
    }
    const vat = el('p', 'xr-plan-vat')
    vat.dataset.price = priceIdFor(planPriceKey(planId, 'year', use)) ?? ''
    vat.dataset.interval = 'year'
    const price = plan.commercialYearly !== undefined ? t('xr.plans.perYear', { amount: money(quote(planId, use).total ?? 0) }) : priceText(plan)
    into.append(el('p', 'xr-plan-price', price), vat)
  }

  /** What a choice includes in live data, in words. */
  function packagesText(choice: PlanChoice): string {
    return XR_PLANS[choice.plan].packages === 'live-highlights' ? t('xr.plans.cap.liveHighlights') : t('xr.mine.packagesAll')
  }

  function renderPlans(): void {
    d.body.replaceChildren()
    if (opts.notice) d.body.append(el('p', 'xr-plans-notice', opts.notice))
    d.body.append(el('p', 'xr-plans-lead', t('xr.plans.lead')))
    const tabs = el('div', 'xr-plans-tabs')
    tabs.setAttribute('role', 'tablist')
    tabs.setAttribute('aria-label', t('xr.plans.segments.aria'))
    // AYNI (owner, 2026-10-18): Venue is not shown, unless it is the plan held.
    for (const s of current === 'venue' ? XR_SEGMENTS : PUBLIC_SEGMENTS) {
      const tab = button(`xr-plans-tab${s === current ? ' selected' : ''}`, t(SEGMENT_WORDS[s].name))
      tab.setAttribute('role', 'tab')
      tab.setAttribute('aria-selected', String(s === current))
      tab.addEventListener('click', () => {
        current = s
        renderPlans()
        ;(d.body.querySelector('.xr-plans-tab.selected') as HTMLElement | null)?.focus()
      })
      tabs.append(tab)
    }
    d.body.append(tabs, el('p', 'xr-plans-segment-lead', t(SEGMENT_WORDS[current].lead)))
    const held = heldPlansRow()
    if (held) d.body.insertBefore(held, tabs)

    const cards = el('div', 'xr-plans-cards')
    if (current === 'public') cards.append(freeCard())
    for (const plan of plansOf(current)) cards.append(planCard(plan))
    d.body.append(cards)
    // AYNI (owner, 2026-10-18): pilots are talked through, not priced in public.
    if (current === 'situation') d.body.append(el('p', 'xr-plans-note xr-plans-pilot', t('xr.plans.situationPilot')))
    if (current === 'research') d.body.append(el('p', 'xr-plans-note xr-plans-academic', t('xr.plans.academicEmail')))
    d.body.append(current === 'public' ? highlightsBox() : packageList())
    if (current === 'media') d.body.append(el('p', 'xr-plans-note', t('pricing.credits', { n: formatNumber(CREDIT_PACK.credits), eur: money(CREDIT_PACK.eur) })))
    d.body.append(el('p', 'xr-plans-footer', t(online ? 'xr.plans.footerOnline' : 'xr.plans.footer')))
    fillVat()
  }

  /**
   * AYNI (decision D12, made possible by several licences, 2026-10-10): with
   * two current licences or more, a row of the plans held, the one in use
   * marked, each opening its own tab.
   */
  function heldPlansRow(): HTMLElement | null {
    const account = accountState()
    const held = account.status === 'signedIn' ? account.licences.filter((l) => l.status === 'active' && l.plan) : []
    if (held.length < 2) return null
    const row = el('div', 'xr-plans-held')
    row.append(el('span', 'xr-plans-held-label', t('xr.plans.held')))
    for (const l of held) {
      const plan = XR_PLANS[l.plan!]
      const go = button(`xr-plans-held-plan${plan.segment === current ? ' selected' : ''}`, l.inUse ? t('xr.plans.heldInUse', { plan: t(PLAN_NAME[plan.id]) }) : t(PLAN_NAME[plan.id]))
      go.addEventListener('click', () => {
        current = plan.segment
        renderPlans()
        d.body.querySelector<HTMLElement>(`.xr-plan-card[data-plan="${plan.id}"]`)?.scrollIntoView({ block: 'nearest' })
      })
      row.append(go)
    }
    return row
  }

  function freeCard(): HTMLElement {
    const card = el('article', 'xr-plan-card xr-plan-free')
    card.append(el('h3', '', t('xr.plans.free.name')), el('p', 'xr-plan-price', t('xr.plans.free.price')), el('p', 'xr-plan-seats', t('xr.plans.free.who')))
    const list = el('ul', 'xr-plan-lines')
    for (const key of ['xr.plans.free.sos', 'xr.plans.free.join', 'xr.plans.free.rooms', 'xr.plans.free.globes'] as const) list.append(el('li', '', t(key)))
    card.append(list, el('p', 'xr-plan-badge', t('xr.plans.free.current')))
    return card
  }

  function planCard(plan: XrPlan): HTMLElement {
    if (online && planOnline(plan.id)) return onlineCard(plan, plan.id)
    const card = el('article', 'xr-plan-card')
    card.dataset.plan = plan.id
    card.append(el('h3', '', t(PLAN_NAME[plan.id])), el('p', 'xr-plan-price', priceText(plan)))
    for (const note of priceNotes(plan)) card.append(el('p', 'xr-plan-monthly', note))
    card.append(el('p', 'xr-plan-seats', seatsText(plan)))
    const list = el('ul', 'xr-plan-lines')
    for (const line of planLines(plan)) list.append(el('li', '', line))
    card.append(list)
    const cta = button('xr-plan-cta', t(plan.yearly === null ? 'xr.plans.quoteRequest' : 'xr.plans.request'))
    cta.addEventListener('click', () => renderRequest(plan.id))
    card.append(cta)
    return card
  }

  /**
   * AYNI (phase 4): a plan sold online — its period (Plus), the price before
   * VAT and with the visitor's VAT, Buy; the plans for organisations can
   * still ask for an invoice.
   */
  function onlineCard(plan: XrPlan, planId: OnlinePlanId): HTMLElement {
    const card = el('article', 'xr-plan-card')
    card.dataset.plan = plan.id
    card.append(el('h3', '', t(PLAN_NAME[plan.id])))
    if (planId === 'plus') card.append(intervalToggle(renderPlans))
    if (plan.commercialYearly !== undefined) card.append(useToggle(renderPlans))
    onlinePrice(planId, card)
    if (plan.ownDataYearly) card.append(el('p', 'xr-plan-monthly', t('xr.plans.ownDataSoon', { academic: money(plan.ownDataYearly.academic), commercial: money(plan.ownDataYearly.commercial) })))
    card.append(el('p', 'xr-plan-seats', seatsText(plan)))
    const list = el('ul', 'xr-plan-lines')
    for (const line of planLines(plan)) list.append(el('li', '', line))
    card.append(list)
    const cta = button('xr-plan-cta', t('xr.plans.buy'))
    cta.addEventListener('click', () => renderBuy(planId))
    card.append(cta)
    if (planId !== 'plus') {
      const invoice = button('xr-plan-invoice', t('xr.plans.orInvoice'))
      invoice.addEventListener('click', () => renderRequest(plan.id))
      card.append(invoice)
    }
    return card
  }

  /**
   * AYNI (phase 4): buying a plan online — what is bought, the period, the
   * price before VAT and with the visitor's VAT, then sign in if needed, then
   * Paddle's checkout over the page.
   */
  function renderBuy(planId: OnlinePlanId): void {
    d.body.replaceChildren()
    const back = button('xr-plans-back', `← ${t('xr.request.back')}`)
    back.addEventListener('click', () => renderPlans())
    const choice = choiceFor(planId)
    const plan = XR_PLANS[planId]
    const summary = el('div', 'xr-request-summary xr-buy-summary')
    summary.append(el('p', 'xr-plans-note', t('xr.buy.summary')), el('h3', '', t(PLAN_NAME[planId])))
    summary.append(el('p', 'xr-plans-note xr-buy-packages', `${seatsText(plan)} · ${packagesText(choice)}`))
    if (planId === 'plus') {
      summary.append(intervalToggle(() => renderBuy(planId)))
      onlinePrice(planId, summary)
    } else {
      if (plan.commercialYearly !== undefined) summary.append(useToggle(() => renderBuy(planId)))
      summary.append(el('p', 'xr-plan-price', t('xr.plans.perYear', { amount: money(quote(planId, choice.use).total ?? 0) })))
      const vat = el('p', 'xr-plan-vat xr-buy-vat')
      summary.append(vat)
      void previewChoice(choice).then((lines) => {
        if (!lines?.length || !vat.isConnected) return
        vat.textContent = t('xr.plans.withVatYear', { amount: lines[0] })
      })
    }
    if ((plan.seats ?? 1) > 1) summary.append(el('p', 'xr-plans-note xr-buy-seats', t('xr.buy.seatsNote', { n: plan.seats ?? 1 })))
    const notice = el('p', 'xr-plans-note', `${t('xr.buy.paddle')} `)
    const link = el('a', '', t('xr.request.noticeLink'))
    link.href = '/privacy'
    link.target = '_blank'
    link.rel = 'noopener'
    // AYNI (2026-10-15): the terms of use, refunds included, beside the privacy policy.
    const terms = el('a', 'xr-buy-terms', t('account.menu.terms'))
    terms.href = t('legal.termsUrl')
    terms.target = '_blank'
    terms.rel = 'noopener'
    notice.append(link, ' · ', terms)
    const status = el('p', 'xr-request-status')
    status.setAttribute('role', 'status')
    d.body.append(back, summary, notice)
    fillVat()
    const account = accountState()
    if (account.status !== 'signedIn') {
      d.body.append(el('p', 'xr-plans-lead', t('xr.buy.signInFirst')), signInForm())
      return
    }
    if (account.billing && account.billing.status !== 'canceled' && !account.billing.revokedAt) {
      d.body.append(el('p', 'xr-plans-lead', t('xr.buy.already')), manageButton(status), status)
      return
    }
    const go = button('xr-plan-cta xr-buy-continue', t('xr.buy.continue'))
    go.addEventListener('click', () => {
      go.disabled = true
      go.textContent = t('xr.buy.opening')
      status.textContent = ''
      void startCheckout(choice, getLocale(), {
        onCompleted: () => renderPaid(),
        onClosed: () => {
          go.disabled = false
          go.textContent = t('xr.buy.continue')
        },
      }).then((result) => {
        if (result.ok) return
        go.disabled = false
        go.textContent = t('xr.buy.continue')
        status.textContent = t(CHECKOUT_ERROR[result.error])
      })
    })
    d.body.append(go, status)
    go.focus()
  }

  /** After Paddle took the payment: the licence follows from its webhook. */
  function renderPaid(): void {
    d.body.replaceChildren(el('h3', '', t('xr.buy.done.title')), el('p', 'xr-request-done', t('xr.buy.done.body')))
    const mine = button('xr-plan-cta', t('xr.buy.done.mine'))
    mine.addEventListener('click', () => openYourPlan(trigger, opts))
    d.body.append(mine)
    void loadAccount()
  }

  function packageList(): HTMLElement {
    const section = el('section', 'xr-plans-packages')
    section.append(el('h3', '', t('xr.plans.packages.title')))
    const list = el('ul', 'xr-package-list')
    for (const p of XR_PACKAGES) {
      const item = el('li', 'xr-package')
      item.append(el('strong', '', t(PACKAGE_WORDS[p].name)), el('span', '', t(PACKAGE_WORDS[p].desc)))
      list.append(item)
    }
    section.append(list)
    return section
  }

  /** Plus's streams (owner, 2026-10-17): Live Earth and the climate highlights, in words. */
  function highlightsBox(): HTMLElement {
    const section = el('section', 'xr-plans-packages')
    section.append(el('h3', '', t('xr.plans.highlights.title')), el('p', 'xr-plans-note', t('xr.plans.highlights.body')))
    return section
  }

  function renderRequest(planId: XrPlanId): void {
    const plan = XR_PLANS[planId]
    const planUse: PlanUse = plan.commercialYearly !== undefined ? use : 'academic'
    const q = quote(planId, planUse)
    d.body.replaceChildren()
    const back = button('xr-plans-back', `← ${t('xr.request.back')}`)
    back.addEventListener('click', () => renderPlans())
    const summary = el('div', 'xr-request-summary')
    summary.append(
      el('p', 'xr-plans-note', t('xr.request.summary')),
      el('h3', '', t(PLAN_NAME[planId])),
      el('p', 'xr-plan-price', q.total === null ? t('xr.plans.byQuote') : t('xr.plans.perYear', { amount: money(q.total) })),
    )
    if (plan.commercialYearly !== undefined) summary.append(el('p', 'xr-plans-note', t(planUse === 'commercial' ? 'xr.plans.use.commercial' : 'xr.plans.use.academic')))
    const form = el('form', 'xr-request-form')
    form.noValidate = true
    const field = (name: string, label: string, type = 'text', required = false, max = 120): HTMLInputElement => {
      const wrap = el('label', 'xr-request-field')
      const input = el('input')
      input.name = name
      input.type = type
      input.maxLength = max
      input.required = required
      wrap.append(el('span', '', label), input)
      form.append(wrap)
      return input
    }
    const organisation = field('organisation', t('xr.request.organisation'), 'text', true)
    const name = field('name', t('xr.request.name'), 'text', true)
    const email = field('email', t('xr.request.email'), 'email', true, 200)
    const country = field('country', t('xr.request.country'), 'text', true, 60)
    const vat = field('vat', t('xr.request.vat'), 'text', false, 40)
    const po = field('po', t('xr.request.po'), 'text', false, 60)
    let seats: HTMLInputElement | null = null
    if (plan.seats === null || plan.seats > 1) {
      seats = field('seats', t('xr.request.seats'), 'number')
      seats.min = '1'
      seats.max = '10000'
      seats.value = String(plan.seats ?? 10)
    }
    const messageWrap = el('label', 'xr-request-field')
    const message = el('textarea')
    message.name = 'message'
    message.maxLength = 1000
    message.rows = 3
    messageWrap.append(el('span', '', t('xr.request.message')), message)
    form.append(messageWrap)
    const notice = el('p', 'xr-plans-note', `${t('xr.request.notice')} `)
    const link = el('a', '', t('xr.request.noticeLink'))
    link.href = '/privacy'
    link.target = '_blank'
    link.rel = 'noopener'
    notice.append(link)
    const status = el('p', 'xr-request-status')
    status.setAttribute('role', 'status')
    const send = el('button', 'xr-plan-cta', t('xr.request.send'))
    send.type = 'submit'
    // Security fix S-04: a Turnstile check when the build has a site key; shown only when it must be.
    const challenge = attachChallenge({ action: 'plan-request' })
    form.append(notice, status, ...(challenge ? [challenge.element] : []), send)
    form.addEventListener('submit', (ev) => {
      ev.preventDefault()
      if (!organisation.value.trim() || !name.value.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()) || !country.value.trim()) {
        status.textContent = t('xr.request.missing')
        return
      }
      send.disabled = true
      send.textContent = t('xr.request.sending')
      status.textContent = ''
      void (challenge ? challenge.token() : Promise.resolve(null)).then((turnstileToken) => post({
        plan: planId,
        use: planUse,
        seats: seats ? Number(seats.value) || null : plan.seats,
        organisation: organisation.value.trim(),
        name: name.value.trim(),
        email: email.value.trim(),
        country: country.value.trim(),
        vat: vat.value.trim(),
        po: po.value.trim(),
        message: message.value.trim(),
        ...(turnstileToken ? { turnstileToken } : {}),
      })).then((reply) => {
        if (reply.ok && typeof reply.body.reference === 'string') {
          renderDone(reply.body.reference, email.value.trim())
          return
        }
        send.disabled = false
        send.textContent = t('xr.request.send')
        status.textContent = t(reply.body.error === 'challenge_failed' ? 'xr.request.challengeFailed' : reply.body.error === 'too_many' ? 'xr.buy.error.tooMany' : 'xr.request.failed')
      })
    })
    d.body.append(back, summary, form)
    organisation.focus()
  }

  function renderDone(reference: string, address: string): void {
    d.body.replaceChildren(
      el('h3', '', t('xr.request.done.title')),
      el('p', 'xr-request-done', t('xr.request.done.body', { ref: reference, email: address })),
    )
    const back = button('xr-plans-back', `← ${t('xr.request.back')}`)
    back.addEventListener('click', () => renderPlans())
    d.body.append(back)
  }

  renderPlans()
}

// ------------------------------------------------------------------ sign in

/**
 * The sign-in choices: Google, and an emailed link. The first sign-in
 * creates the account. Shown only where this site offers them.
 */
function signInForm(message = ''): HTMLElement {
  const box = el('div', 'xr-signin')
  const { signIn } = accountState()
  const status = el('p', 'xr-request-status', message)
  status.setAttribute('role', 'status')
  box.append(el('p', 'xr-plans-lead', t('account.signIn.lead')))
  if (signIn.google) {
    const google = el('a', 'xr-plan-cta xr-signin-google', t('account.signIn.google'))
    google.href = GOOGLE_SIGN_IN
    box.append(google)
  }
  if (signIn.google && signIn.email) box.append(el('p', 'xr-signin-or', t('account.signIn.or')))
  if (signIn.email) {
    const form = el('form', 'xr-request-form xr-signin-email')
    form.noValidate = true
    const wrap = el('label', 'xr-request-field')
    const email = el('input')
    email.name = 'email'
    email.type = 'email'
    email.autocomplete = 'email'
    email.maxLength = 200
    wrap.append(el('span', '', t('account.signIn.emailLabel')), email)
    const send = el('button', signIn.google ? 'xr-plans-secondary' : 'xr-plan-cta', t('account.signIn.emailSend'))
    send.type = 'submit'
    // Security fix S-04: a Turnstile check when the build has a site key; shown only when it must be.
    const challenge = attachChallenge({ action: 'signin' })
    form.append(wrap, ...(challenge ? [challenge.element] : []), send)
    form.addEventListener('submit', (ev) => {
      ev.preventDefault()
      const address = email.value.trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
        status.textContent = t('account.signIn.error.invalidEmail')
        return
      }
      send.disabled = true
      send.textContent = t('account.signIn.sending')
      status.textContent = ''
      void (challenge ? challenge.token() : Promise.resolve(null)).then((token) => requestSignInLink(address, fetch, token)).then((result) => {
        send.disabled = false
        send.textContent = t('account.signIn.emailSend')
        if (!result.ok) {
          status.textContent = t(SIGN_IN_ERROR[result.error])
          return
        }
        form.replaceWith(el('p', 'xr-signin-sent', t('account.signIn.sent', { email: address })))
        if (result.devLink) {
          const dev = el('a', 'xr-signin-dev', t('account.signIn.devLink'))
          dev.href = result.devLink
          box.append(dev)
        }
      })
    })
    box.append(form)
    setTimeout(() => email.focus(), 0) // tick-drain-exempt: after the dialog has taken focus
  }
  if (!signIn.google && !signIn.email) box.append(el('p', 'xr-plans-note', t('account.signIn.unavailable')))
  const note = el('p', 'xr-plans-note', `${t('account.signIn.privacy')} `)
  const link = el('a', '', t('xr.request.noticeLink'))
  link.href = '/privacy'
  link.target = '_blank'
  link.rel = 'noopener'
  note.append(link)
  box.append(status, note)
  return box
}

/** Open the sign-in dialog, saying `error` when a sign-in just failed. */
export function openSignIn(trigger: HTMLElement | null = null, error?: SignInError): void {
  const d = dialog(t('account.signIn.title'), trigger, 'xr-signin-dialog')
  d.body.append(signInForm(error ? t(SIGN_IN_ERROR[error]) : ''))
}

/** Delete the account, after its owner types its email address. */
export function openDeleteAccount(trigger: HTMLElement | null = null, onDeleted?: () => void): void {
  const d = dialog(t('account.delete.title'), trigger, 'xr-delete-dialog')
  const form = el('form', 'xr-request-form')
  form.noValidate = true
  const wrap = el('label', 'xr-request-field')
  const confirm = el('input')
  confirm.name = 'confirm'
  confirm.type = 'email'
  confirm.autocomplete = 'off'
  wrap.append(el('span', '', t('account.delete.label')), confirm)
  const status = el('p', 'xr-request-status')
  status.setAttribute('role', 'status')
  const go = el('button', 'xr-plan-cta xr-danger', t('account.delete.confirm'))
  go.type = 'submit'
  // AYNI (review M-1): the step-up box (a passkey, or an emailed code) appears here when the site asks for it.
  const stepSlot = el('div', 'mydata-tab mydata-stepup-slot')
  form.append(wrap, stepSlot, status, go)
  form.addEventListener('submit', (ev) => {
    ev.preventDefault()
    go.disabled = true
    const typed = confirm.value.trim().toLowerCase()
    const attempt = async (): Promise<void> => {
      let result = await deleteAccount(typed)
      if (result === 'step_up') {
        status.textContent = ''
        result = (await askStepUp(stepSlot, 'account.delete')) ? await deleteAccount(typed) : 'failed'
        if (result === 'step_up') result = 'failed'
      }
      go.disabled = false
      if (result === 'deleted') {
        d.body.replaceChildren(el('p', 'xr-plans-lead', t('account.delete.done')))
        onDeleted?.()
      } else {
        status.textContent = t(result === 'mismatch' ? 'account.delete.mismatch' : result === 'subscription' ? 'account.delete.subscription' : 'account.delete.notConfirmed')
      }
    }
    void attempt()
  })
  d.body.append(el('p', 'xr-plans-lead', t('account.delete.body')), form)
  setTimeout(() => confirm.focus(), 0) // tick-drain-exempt: after the dialog has taken focus
}

// ---------------------------------------------------------------- your plan

/** AYNI: "Manage subscription" — Paddle's billing portal in a new tab; `status` says when it cannot open. */
function manageButton(status: HTMLElement): HTMLButtonElement {
  const manage = button('xr-plans-secondary xr-billing-manage', t('xr.mine.billing.manage'))
  manage.addEventListener('click', () => {
    manage.disabled = true
    status.textContent = ''
    void openBillingPortal().then((result) => {
      manage.disabled = false
      if (result !== 'opened') status.textContent = t('xr.mine.billing.portalFailed')
    })
  })
  return manage
}

const dateText = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString(getLocale()) : '—')

/** AYNI: the account's Paddle subscription in "Your plan": what it is, what happens next, and Manage. */
function billingBlock(billing: BillingSummary, licenceHeld: boolean): HTMLElement {
  const box = el('section', 'xr-mine-billing')
  box.append(el('h4', 'xr-mine-billing-title', t('xr.mine.billing.title')))
  const lines = el('ul', 'xr-plan-lines')
  const plan = XR_PLANS[billing.plan as XrPlanId] as XrPlan | undefined
  if (plan) lines.append(el('li', 'xr-mine-billing-plan', `${t(PLAN_NAME[plan.id])} · ${seatsText(plan)}`))
  // No packages are sold since 2026-10-17: the plan says its streams.
  if (plan) lines.append(el('li', '', t(plan.packages === 'live-highlights' ? 'xr.plans.cap.liveHighlights' : 'xr.mine.packagesAll')))
  const state = billing.revokedAt
    ? t(billing.revokedReason === 'chargeback' ? 'xr.mine.billing.chargeback' : 'xr.mine.billing.revoked', { date: dateText(billing.revokedAt) })
    : billing.status === 'canceled'
      ? t('xr.mine.billing.ended', { date: dateText(billing.endedAt) })
      : billing.status === 'paused'
        ? t('xr.mine.billing.paused')
        : billing.status === 'past_due'
          ? t('xr.mine.billing.pastDue', { date: dateText(billing.graceEndsAt) })
          : billing.scheduledAction === 'cancel'
            ? t('xr.mine.billing.cancels', { date: dateText(billing.scheduledAt ?? billing.periodEndsAt) })
            : t(billing.interval === 'month' ? 'xr.mine.billing.renewsMonth' : 'xr.mine.billing.renewsYear', { date: dateText(billing.periodEndsAt) })
  lines.append(el('li', billing.status === 'past_due' || billing.revokedAt ? 'xr-mine-billing-warn' : '', state))
  if (!licenceHeld && !billing.revokedAt && (billing.status === 'active' || billing.status === 'trialing')) lines.append(el('li', '', t('xr.mine.billing.licencePending')))
  const status = el('p', 'xr-request-status')
  status.setAttribute('role', 'status')
  box.append(lines, el('p', 'xr-plans-note', t('xr.mine.billing.manageNote')), manageButton(status))
  // A licence for several people (School, Lab): its buyer shares the key with the others.
  if (billing.seats > 1 && licenceHeld && !billing.revokedAt && billing.status !== 'canceled') box.append(sharedKeyBlock(status))
  box.append(status)
  return box
}

/** AYNI: "Show the key to share", for a licence bought online for several people; the key is asked for on the click. */
function sharedKeyBlock(status: HTMLElement): HTMLElement {
  const wrap = el('div', 'xr-mine-shared-key')
  const show = button('xr-plans-secondary', t('xr.mine.billing.showKey'))
  show.addEventListener('click', () => {
    show.disabled = true
    status.textContent = ''
    void fetchSharedKey().then((result) => {
      show.disabled = false
      if (typeof result === 'string') {
        status.textContent = t(result === 'none' ? 'xr.mine.billing.keyNone' : 'xr.mine.billing.keyFailed')
        return
      }
      const code = el('code', 'xr-mine-shared-key-value', result.key)
      wrap.replaceChildren(code, el('p', 'xr-plans-note', t('xr.mine.billing.keyNote', { seats: result.seats })))
    })
  })
  wrap.append(show)
  return wrap
}

/**
 * Why the licence in use changed by itself (decision M6), in words: which
 * licence went and why, and which one is in use now.
 */
export function licenceNoticeText(notice: LicenceNotice): string {
  const from = notice.fromPlan ? planName(notice.fromPlan) : t('xr.mine.aLicence')
  const went = notice.reason === 'ended'
    ? t('account.notice.ended', { plan: from, hint: notice.fromHint })
    : t('account.notice.refused', { plan: from, hint: notice.fromHint, reason: t(LICENCE_ERROR[notice.reason]) })
  const now = notice.toPlan ? t('account.notice.nowUsing', { plan: planName(notice.toPlan) }) : t('account.notice.nowNone')
  return `${went} ${now}`
}

/**
 * Open "Your plan": the account's licences (several since 2026-10-10, one
 * of them in use; `docs/AYNI_XR_MULTIPLE_LICENCES_PLAN.md`), a place to
 * enter a key, or the way to sign in first.
 */
export function openYourPlan(trigger: HTMLElement | null = null, opts: PlansOptions = {}, message = ''): void {
  const d = dialog(t('xr.mine.title'), trigger, 'xr-mine')

  /** The key field. `another`: the account already holds a licence (a "Use it now" box, other words when done). */
  function keyForm(status: HTMLElement, another: boolean): HTMLFormElement {
    const form = el('form', 'xr-request-form')
    const wrap = el('label', 'xr-request-field')
    const key = el('input')
    key.name = 'key'
    key.autocomplete = 'off'
    key.spellcheck = false
    key.placeholder = t('xr.mine.keyPlaceholder')
    key.maxLength = 40
    wrap.append(el('span', '', t('xr.mine.keyLabel')), key)
    form.append(wrap)
    let useNow: HTMLInputElement | null = null
    if (another) {
      const box = el('label', 'xr-mine-use-now')
      useNow = el('input')
      useNow.type = 'checkbox'
      useNow.name = 'use'
      // Decision M4: presenting a meeting, a new key does not change the licence in use.
      useNow.checked = !licenceSwitchBlocked()
      useNow.disabled = licenceSwitchBlocked()
      box.append(useNow, el('span', '', t('xr.mine.useNow')))
      form.append(box)
    }
    const go = el('button', 'xr-plan-cta', t('xr.mine.activate'))
    go.type = 'submit'
    form.append(go)
    form.addEventListener('submit', (ev) => {
      ev.preventDefault()
      if (!key.value.trim()) return
      go.disabled = true
      go.textContent = t('xr.mine.activating')
      void addLicenceKey(key.value, undefined, useNow ? useNow.checked : true).then((result) => {
        if (result.ok) {
          render(t(!another ? 'xr.mine.activated' : result.already ? 'xr.mine.already' : useNow?.checked === false ? 'xr.mine.addedKept' : 'xr.mine.added'))
        } else if (result.error === 'signed_out') {
          render(t('xr.mine.error.signedOut'))
        } else {
          go.disabled = false
          go.textContent = t('xr.mine.activate')
          status.textContent = t(LICENCE_ERROR[result.error])
        }
      })
    })
    return form
  }

  /** One licence: its plan, its state, the facts, and what can be done with it. */
  function licenceCard(account: AccountState, licence: LicenceSummary, status: HTMLElement): HTMLElement {
    const several = account.licences.length > 1
    const card = el('section', `xr-mine-licence${licence.inUse && several ? ' in-use' : ''}`)
    const plan = licence.plan ? XR_PLANS[licence.plan] : null
    const head = el('div', 'xr-mine-licence-head')
    head.append(el('h3', 'xr-mine-plan', plan ? t(PLAN_NAME[plan.id]) : '—'))
    if (licence.inUse && several) head.append(el('span', 'xr-mine-badge', t('xr.mine.inUse')))
    card.append(head)
    const state = licence.status === 'expired' ? t('xr.mine.expired') : licence.status === 'lease-expired' ? t('xr.mine.lease') : !plan ? t('xr.mine.noPlan') : ''
    if (state) card.append(el('p', 'xr-mine-state', state))
    const known = licence.packages.filter((p): p is XrPackage => (XR_PACKAGES as readonly string[]).includes(p))
    const facts = el('ul', 'xr-plan-lines')
    facts.append(
      el('li', '', t('xr.mine.holder', { name: licence.holder })),
      el('li', '', t('xr.mine.key', { hint: licence.hint })),
      el('li', '', t('xr.mine.renews', { date: new Date(licence.paidUntil * 1000).toLocaleDateString(getLocale()) })),
      el('li', '', plan?.packages === 'live-highlights'
        ? t('xr.plans.cap.liveHighlights')
        : plan && (plan.packages === 'all' || plan.packages === 'all-hazards') || licence.packages.includes('*')
        ? t('xr.mine.packagesAll')
        : known.length === 0
          ? t('xr.mine.packagesNone')
          : t('xr.mine.packages', { list: known.map((p) => t(PACKAGE_WORDS[p].name)).join(', ') })),
    )
    // What the plan includes: for the licence in use only, so a list of several stays short.
    if (plan && (licence.inUse || !several)) for (const line of planLines(plan)) facts.append(el('li', '', line))
    const extras = plan && (licence.inUse || !several) ? extrasLine(licence.caps) : null
    // AYNI (owner, 2026-10-06): a plan with a daily stream cap shows today's use, on the licence in use.
    const quota = licence.inUse !== false && licence.status === 'active' ? streamQuotaToday() : null
    if (quota && quota.capMB !== null) {
      facts.append(el('li', 'xr-mine-stream', t('xr.mine.streamToday', { used: volumeText(quota.usedMB), cap: volumeText(quota.capMB) })))
    }
    if (extras) facts.append(el('li', 'xr-plan-extras', extras))
    card.append(facts)
    const actions = el('div', 'xr-mine-actions')
    if (licence.inUse !== false && licence.status === 'active' && plan && plan.meetings > 0 && opts.onStartMeeting) {
      const start = button('xr-plan-cta', t('xr.mine.startMeeting'))
      start.addEventListener('click', () => {
        d.close()
        opts.onStartMeeting?.(trigger ?? start)
      })
      actions.append(start)
    }
    if (!licence.inUse && licence.status === 'active' && licence.ref) {
      const use = button('xr-plan-cta', t('xr.mine.use'))
      const blocked = licenceSwitchBlocked()
      use.disabled = blocked
      if (blocked) use.title = t('xr.mine.switchBlocked')
      use.addEventListener('click', () => {
        use.disabled = true
        use.textContent = t('xr.mine.switching')
        void useLicence(licence.ref!).then((result) => {
          if (result === 'ok') render(t('xr.mine.switched', { plan: plan ? t(PLAN_NAME[plan.id]) : '—' }))
          else if (result === 'blocked') status.textContent = t('xr.mine.switchBlocked')
          else render(t(result === 'not_current' ? 'xr.mine.notCurrent' : 'xr.mine.error.unknown'))
        })
      })
      actions.append(use)
      if (blocked) card.append(el('p', 'xr-plans-note', t('xr.mine.switchBlocked')))
    }
    const remove = button('xr-plans-secondary', t('xr.mine.remove'))
    // Decision M4: removing the licence in use would change it; not while presenting a meeting.
    if (licence.inUse && several && licenceSwitchBlocked()) {
      remove.disabled = true
      remove.title = t('xr.mine.switchBlocked')
    }
    remove.addEventListener('click', () => {
      remove.disabled = true
      void removeLicenceKey(undefined, licence.ref).then((done) => render(t(done === 'ok' ? 'xr.mine.removed' : 'xr.mine.switchBlocked')))
    })
    actions.append(remove)
    card.append(actions)
    return card
  }

  function render(note = ''): void {
    const account = accountState()
    d.body.replaceChildren()
    if (account.status !== 'signedIn') {
      d.body.append(el('p', 'xr-plans-lead', t('xr.mine.signInFirst')), signInForm(note))
      return
    }
    const status = el('p', 'xr-request-status', note)
    status.setAttribute('role', 'status')
    const licence = account.licence
    const billing = account.billing ? billingBlock(account.billing, !!licence) : null
    if (!licence) {
      if (billing) d.body.append(billing)
      else d.body.append(el('p', 'xr-plans-lead', t('xr.mine.none')))
      if (account.refused && !note) status.textContent = t(LICENCE_ERROR[account.refused])
      const form = keyForm(status, false)
      d.body.append(form, status)
      setTimeout(() => (form.querySelector('input') as HTMLInputElement | null)?.focus(), 0) // tick-drain-exempt: after the dialog has taken focus
    } else {
      if (account.notice && !note) status.textContent = licenceNoticeText(account.notice)
      const count = account.licences.length
      if (count > 1) d.body.append(el('p', 'xr-mine-count', t('xr.mine.licencesTitle', { count, max: account.maxLicences })))
      d.body.append(status)
      for (const held of account.licences) d.body.append(licenceCard(account, held, status))
      // Several licences on one account (owner, 2026-10-10): another key, up to the most an account may hold.
      if (account.maxLicences > 1) {
        if (count < account.maxLicences) {
          const more = el('details', 'xr-mine-more')
          more.append(el('summary', '', t('xr.mine.addAnother')), keyForm(status, true))
          d.body.append(more)
        } else {
          d.body.append(el('p', 'xr-plans-note', t('xr.mine.full', { max: account.maxLicences })))
        }
      }
      if (billing) d.body.append(billing)
    }
    d.body.append(el('p', 'xr-plans-note xr-mine-who', t('xr.mine.signedInAs', { email: account.email ?? '' })))
    const plans = button('xr-plans-secondary', t('xr.mine.seePlans'))
    const segment = licence?.plan ? XR_PLANS[licence.plan].segment : 'public'
    plans.addEventListener('click', () => openPlans(trigger, segment, opts))
    d.body.append(plans)
  }

  render(message)
}

/** The segment a `?plans=` address names, or null when the address does not ask for the plans. */
export function plansFromSearch(search: string = typeof window === 'undefined' ? '' : window.location.search): XrSegment | 'default' | null {
  const params = new URLSearchParams(search)
  if (!params.has('plans')) return null
  const asked = params.get('plans') as XrSegment
  return PUBLIC_SEGMENTS.includes(asked) ? asked : 'default'
}
